import React from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { addExternalUserContact, getExternalUserContacts } from '../../../../core/state/subcontractors/asyncActions';
import { RootState } from '../../../../core/state/state';
import { Contact } from '../../../../core/state/subcontractors/types';
import { NotificationPosition } from '../../../../core/state/global-notifications/types';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { Color } from '../../../../core/enums/Color';
import Input from '../../../../components/form/input/Input';
import CheckBox from '../../../../components/form/checkbox/CheckBox';
import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { isMobileScreenSize } from '../../../../core/utils/isMobileScreenSize';

import './AddCompanyContactModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  notificationSuccessTitle?: string;
  notificationSuccessMessage?: string;
  notificationPosition?: NotificationPosition;
  onHandleContact?: (response: string) => void;
  isMobile?: boolean;
}

const AddCompanyContactModal: React.FC<Props> = ({
  isOpen,
  closeModal,
  notificationSuccessTitle = '',
  notificationSuccessMessage = '',
  onHandleContact,
  notificationPosition = NotificationPosition.Top,
  isMobile
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isMobileScreen = isMobileScreenSize();

  const userID = useSelector((state: RootState) => state.user.userInfo?.userID);
  const validationSchema = yup
    .object()
    .shape({
      firstName: yup.string().required(t('validation.firstName.required')),
      lastName: yup.string().required(t('validation.lastName.required'))
    })
    .required();

  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<Contact>({
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      isPrimary: false
    },
    resolver: yupResolver(validationSchema)
  });

  const onSubmit = async (formData: Contact) => {
    const response = await dispatch<any>(addExternalUserContact(formData, Number(userID)));
    await dispatch(getExternalUserContacts(Number(userID)));
    if (onHandleContact) {
      onHandleContact(response.data.content.id);
    }
    dispatch(
      spawnGlobalNotification({
        type: GlobalNotificationTypes.Success,
        title: notificationSuccessTitle,
        message: notificationSuccessMessage,
        postition: notificationPosition
      })
    );
    closeModal();
  };
  const title = <h3 className="modal-title">{t('common.addNew')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={!isMobileScreen ? ['transparent'] : ['outline', 'block']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="add-company-contact" type="submit" buttonTypes={!isMobileScreen ? ['blue'] : ['blue', 'block']}>
        {t('common.confirm')}
      </Button>
    </div>
  );

  return (
    <Modal isFullScreen={isMobile} isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={400}>
      <form
        id="add-company-contact"
        className={`add-company-contact ${isMobileScreen ? 'add-company-contact--mobile' : ''}`}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className="add-company-contact__content">
          <Controller
            name="firstName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                required
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="firstName"
                error={errors.firstName?.message}
                name="firstName"
                label={t('company.firstName')}
                placeholder={t('company.placeholder.enterFirstName')}
                value={value}
              />
            )}
          />
          <Controller
            name="lastName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                required
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="lastName"
                error={errors.lastName?.message}
                name="lastName"
                label={t('company.lastName')}
                placeholder={t('common.enterLastName')}
                value={value}
              />
            )}
          />
          <Controller
            name="phoneNumber"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="phoneNumber"
                error={errors.phoneNumber?.message}
                name="phoneNumber"
                label={t('common.companyNumber')}
                placeholder={t('company.modal.placeholder.contactNumber')}
                value={value}
              />
            )}
          />
          <p className="form-label mt-3 add-company-contact__primary-contact">{t('company.modal.primaryContact')}</p>
          <Controller
            name="isPrimary"
            control={control}
            render={({ field: { onChange, value } }) => (
              <CheckBox id="isPrimary" name="isPrimary" checked={value ?? true} onChange={onChange} label={t('company.modal.setContactAsPrimary')} />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default AddCompanyContactModal;
